import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Paper } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import IndexedContent from './IndexedContent';
import './Settings.css';
import { adminConfig, isAdminUser } from '../../config/adminConfig';
import { useAuthToken } from '../../contexts/AuthTokenContext';

export type SettingsType = 'api' | 'all-documents' | 'documents' | 'websites';

interface Props {
  apiUrl: string;
  onApiUrlChange: (url: string) => void;
  onSave: () => void;
  onCancel: () => void;
  userId: string;
  config: {
    apiUrl: string;
  };
  settingsType: SettingsType;
}

export const Settings: React.FC<Props> = ({ 
  apiUrl, 
  onApiUrlChange, 
  onSave, 
  onCancel,
  userId,
  config,
  settingsType
}) => {
  const { t } = useTranslation();
  const { account } = useAuthToken();
  const isAdmin = isAdminUser(account?.username || '');

  if (!adminConfig.allowSettingsTabForUsers && !isAdmin) {
    return null;
  }

  const renderContent = () => {
    switch (settingsType) {
      case 'api':
        return (
          <div className="settings-form">
            <div className="settings-form-field">
              <label htmlFor="api-url">{t('settings.apiUrl')}</label>
              <input
                id="api-url"
                type="text"
                value={apiUrl}
                onChange={(e) => onApiUrlChange(e.target.value)}
                placeholder="http://localhost:7071"
              />
            </div>
            <div className="settings-actions">
              <button className="save-button" onClick={onSave}>
                {t('settings.save')}
              </button>
            </div>
          </div>
        );
      case 'all-documents':
      case 'documents':
      case 'websites':
        return <IndexedContent 
          config={config} 
          userId={userId} 
          filterType={settingsType === 'documents' ? 'document' : settingsType === 'websites' ? 'web' : undefined}
        />;
      default:
        return null;
    }
  };

  const getTitle = () => {
    switch (settingsType) {
      case 'api':
        return t('settings.apiSettings');
      case 'all-documents':
        return t('settings.allDocuments');
      case 'documents':
        return t('settings.documents');
      case 'websites':
        return t('settings.websites');
      default:
        return t('settings.title');
    }
  };

  return (
    <Box sx={{ width: '100%', typography: 'body1' }}>
      <div className="settings-container">
        <div className="settings-header">
          <Typography variant="h5" component="h2">
            {getTitle()}
          </Typography>
          <div className="settings-info">
            {isAdmin && (
              <Typography variant="body2" component="span" sx={{ mr: 2 }}>
                {t('settings.adminStatus')}: {t('common.yes')}
              </Typography>
            )}
          </div>
        </div>
        
        <Paper elevation={2} sx={{ mt: 2, p: 3 }}>
          {renderContent()}
        </Paper>
      </div>
    </Box>
  );
}; 